import { BarChartOut } from "@/api";
import { formatValue, UsageUnit } from "./format";

export type LabelFor = (item: { key: string; label: string }) => string;
export const plainLabel: LabelFor = (item) => item.label;

export type UsageColumn = { key: string; label: string; unit: UsageUnit };
export type UsageRow = { key: string; label: string; value: number; cells: Record<string, number> };

/** Every chart is shown as one of these: a row per bar, a column per breakdown item. */
export type UsageTable = {
    unit: UsageUnit;
    rowLabel: string;
    valueLabel?: string;
    columns: UsageColumn[];
    rows: UsageRow[];
};

export type ChartState = {
    key: string;
    title: string;
    loading: boolean;
    failed: boolean;
    hint?: string;
    table?: UsageTable;
};

export const barTable = (
    chart: BarChartOut,
    options: { rowLabel: string; valueLabel?: string; rowLabelFor?: LabelFor; columnLabelFor?: LabelFor },
): UsageTable => {
    const rowLabelFor = options.rowLabelFor ?? plainLabel;
    const columnLabelFor = options.columnLabelFor ?? plainLabel;
    const columns: UsageColumn[] = [];
    const seen = new Set<string>();
    for (const item of [...(chart.breakdown ?? []), ...chart.bars.flatMap((bar) => bar.breakdown ?? [])]) {
        if (seen.has(item.key)) continue;
        seen.add(item.key);
        columns.push({ key: item.key, label: columnLabelFor(item), unit: item.unit ?? chart.unit });
    }
    return {
        unit: chart.unit,
        rowLabel: options.rowLabel,
        valueLabel: options.valueLabel,
        columns,
        rows: chart.bars.map((bar) => ({
            key: bar.key,
            label: rowLabelFor(bar),
            value: bar.value,
            cells: Object.fromEntries((bar.breakdown ?? []).map((item) => [item.key, item.value])),
        })),
    };
};

export const searchTerms = (search: string) => search.toLowerCase().split(/\s+/).filter(Boolean);

/** Every term must appear in the row's label or one of its shown values. */
export const rowMatches = (table: UsageTable, row: UsageRow, terms: string[]) => {
    if (!terms.length) return true;
    const text = [
        row.label,
        formatValue(row.value, table.unit),
        ...table.columns.map((column) => formatValue(row.cells[column.key] ?? 0, column.unit)),
    ]
        .join(" ")
        .toLowerCase();
    return terms.every((term) => text.includes(term));
};

export const visibleRows = (table: UsageTable, terms: string[]) =>
    table.rows.filter((row) => rowMatches(table, row, terms));

/** The chart is left out when a search is active and none of its rows match. */
export const chartVisible = (chart: ChartState, terms: string[]) =>
    !terms.length || !chart.table || visibleRows(chart.table, terms).length > 0;

export const totals = (table: UsageTable, rows: UsageRow[]) => ({
    value: rows.reduce((sum, row) => sum + row.value, 0),
    columns: table.columns.map((column) => ({
        ...column,
        value: rows.reduce((sum, row) => sum + (row.cells[column.key] ?? 0), 0),
    })),
});
